/**
 * Retrieve the list of files for further process
 * @param {FileCollection[]} fileList
 * @param {object} [options={}]
 * @param {number|number[]} [options.processingNumber] - processing number to select, default the smallest number
 * @param {number|number[]} [options.experimentNumber] - experiment number to select, default all
 * @param {boolean} [options.ignoreFID=false] - should we ignore fid
 * @param {boolean} [options.ignoreFT=false] - should we ignore the ft transformed fid
 * @param {boolean} [options.ignore1D=false] - should we ignore 1D spectra
 * @param {boolean} [options.ignore2D=false] - should we ignore 2D spectra
 * @param {boolean} [options.onlyFirstProcessedData=true] - should we take only the first processed data (procno).
 */
export function groupByExperiments(fileList, options = {}) {
  let {
    processingNumber,
    experimentNumber,
    ignoreFID = false,
    ignoreFT = false,
    ignore1D = false,
    ignore2D = false,
    onlyFirstProcessedData = true,
  } = options;
  if (typeof processingNumber === 'number') {
    processingNumber = [processingNumber];
  }
  if (typeof experimentNumber === 'number') {
    experimentNumber = [experimentNumber];
  }

  const experiments = {};
  for (let file of fileList) {
    let currentProcessingNo;
    let currentExperimentNo;
    let experiment;
    let name;
    let id;
    const parts = file.relativePath.split('/');
    if (file.relativePath.match(/\/pdata\/[0-9]+\/.*$/)) {
      currentProcessingNo = Number(parts[parts.length - 2]);
      currentExperimentNo = Number(parts[parts.length - 4]);
      name = parts[parts.length - 5];
      id = parts.slice(0, -3).join('/');
    } else if (file.relativePath.match(/[0-9]+\/.*$/)) {
      currentExperimentNo = Number(parts[parts.length - 2]);
      name = parts[parts.length - 3] || parts[parts.length - 2];
      id = parts.slice(0, -1).join('/');
    } else {
      continue;
    }

    if (experimentNumber && !experimentNumber.includes(currentExperimentNo)) {
      continue;
    }

    if (!experiments[id]) {
      experiments[id] = {
        name,
        expno: currentExperimentNo,
        processedData: {},
        fileList: [],
      };
    }
    experiment = experiments[id];
    if (currentProcessingNo) {
      if (!experiment.processedData[currentProcessingNo]) {
        experiment.processedData[currentProcessingNo] = {
          fileList: [],
          name,
          expno: currentExperimentNo,
        };
      }
      const processedData = experiment.processedData[currentProcessingNo];
      processedData.fileList.push(file);
      if (file.name.match(/^(1r|1i|2rr|procs|proc2s)$/)) {
        processedData[file.name] = file;
      }
      if (file.name === '2rr') {
        processedData.is2D = true;
        processedData.isFT = true;
      }
      if (file.name === '1r') {
        processedData.is1D = true;
        processedData.isFT = true;
      }
    } else {
      experiment.fileList.push(file);
      if (file.name.match(/^(ser|fid|acqus|acqu2s)$/)) {
        experiment[file.name] = file;
      }
      if (file.name === 'ser') {
        experiment.is2D = true;
        experiment.isFID = true;
      }
      if (file.name === 'fid') {
        experiment.is1D = true;
        experiment.isFID = true;
      }
    }
  }
  if (processingNumber) {
    // we need to deal with the list of processingNumber that is specified
    for (const key in experiments) {
      const processedData = experiments[key].processedData;
      const newProcessedData = {};
      for (let key in processedData) {
        if (!processingNumber.includes(parseInt(key, 10))) continue;
        newProcessedData[key] = processedData[key];
      }
      experiments[key].processedData = newProcessedData;
    }
  } else if (onlyFirstProcessedData) {
    // take the smallest processing and remove all the other
    for (const key in experiments) {
      const processedData = experiments[key].processedData;
      const firstProcessedNumber = Object.keys(processedData).sort(
        (a, b) => Number(a) - Number(b),
      )[0];
      if (firstProcessedNumber !== undefined) {
        experiments[key].processedData = {
          firstProcessedNumber: processedData[firstProcessedNumber],
        };
      }
    }
  }
  // We convert the object to an array
  // and before filtering we will move all the processedData
  let experimentsArray = [];

  for (let key in experiments) {
    const experiment = { ...experiments[key] };
    const processed = experiment.processedData;
    delete experiment.processedData;
    if (experiment.ser || experiment.fid) {
      if (Object.keys(processed).length > 0) {
        const firstProcessed = processed[Object.keys(processed)[0]];
        if (firstProcessed.procs) {
          experiment.fileList.push(firstProcessed.procs);
          experiment.procs = firstProcessed.procs;
        }
        if (firstProcessed.proc2s) {
          experiment.fileList.push(firstProcessed.proc2s);
          experiment.proc2s = firstProcessed.proc2s;
        }
      }
      experimentsArray.push(experiment);
    }
    for (let processedKey in processed) {
      const oneProcessed = processed[processedKey];
      if (oneProcessed['1r'] || oneProcessed['2rr']) {
        if (experiment.acqus) {
          oneProcessed.fileList.push(experiment.acqus);
        }
        if (experiment.acqu2s) {
          oneProcessed.fileList.push(experiment.acqu2s);
        }
        experimentsArray.push({
          acqus: experiment.acqus,
          acqu2s: experiment.acqu2s,
          ...oneProcessed,
        });
      }
    }
  }
  // we can not easily filter
  if (ignoreFID) {
    experimentsArray = experimentsArray.filter((item) => !item.isFID);
  }
  if (ignoreFT) {
    experimentsArray = experimentsArray.filter((item) => !item.isFT);
  }
  if (ignore1D) {
    experimentsArray = experimentsArray.filter((item) => !item.is1D);
  }
  if (ignore2D) {
    experimentsArray = experimentsArray.filter((item) => !item.is2D);
  }

  return experimentsArray;
}
